/**
 * SupplierTable Component
 * 협력사 목록 테이블
 */
import React from 'react';
import { SupplierDetail } from '../types/supplier.types';

/**
 * 등급 뱃지 클래스 반환
 */
const getGradeBadgeClass = (gradeName: string | null | undefined): string => {
    if (!gradeName) return 'badge-default';
    
    const gradeMap: { [key: string]: string } = {
        '마스터': 'badge-master',
        '다이아': 'badge-diamond',
        '골드': 'badge-gold',
        '실버': 'badge-silver',
        '브론즈': 'badge-bronze'
    };
    
    return gradeMap[gradeName] || 'badge-default';
};

// ========================================
// Types
// ========================================
type SortDirection = 'asc' | 'desc' | null;

interface SortConfig {
    columnKey: string | null;
    direction: SortDirection;
}

interface SupplierTableProps {
    suppliers: SupplierDetail[];
    loading: boolean;
    onRowClick: (supplier: SupplierDetail) => void;
    sortConfig: SortConfig;
    onSort: (columnKey: string) => void;
}

/**
 * 테이블 헤더 정의
 */
const TABLE_HEADERS = [
    { key: 'suppType', label: '협력사구분', sortable: true,  width: 120 },
    { key: 'standardArea', label: '표준권역', sortable: false, width: 160 },
    { key: 'slotSetCnt', label: '계약 SET 수', sortable: false, width: 90 },
    { key: 'grade', label: '등급', sortable: true,           width: 120 },
    { key: 'name', label: '협력사명', sortable: true,        width: 200 },
    { key: 'id', label: 'DP코드', sortable: true,            width: 150 },
    { key: 'phoneNumber', label: '협력사연락처', sortable: false, width: 140 },
    { key: 'branchName', label: '지점명', sortable: false,   width: 200 },
    { key: 'ratePlan', label: '요금제', sortable: false,     width: 100 },
    { key: 'registrationDate', label: '등록일자', sortable: true,  width: 120 },
    { key: 'endDate', label: '운영종료일자', sortable: true,       width: 150 },
    { key: 'ownerName', label: '사업주명', sortable: false,  width: 110 },
    { key: 'companyName', label: '상호명', sortable: false,  width: 200 },
    { key: 'businessNumber', label: '사업자등록번호', sortable: false, width: 150 },
    { key: 'accountHolder', label: '예금주', sortable: false, width: 130 },
    { key: 'bankName', label: '은행명', sortable: false,     width: 130 },
    { key: 'accountNumber', label: '계좌번호', sortable: false, width: 150 },
    { key: 'email', label: '이메일주소', sortable: false,    width: 200 },
    { key: 'address', label: '사업자소재지', sortable: false, width: 350 },
    { key: 'bizType', label: '업태', sortable: false,        width: 90  },
    { key: 'bizItem', label: '종목', sortable: false,        width: 90  },
    { key: 'taxType', label: '과세자', sortable: false,      width: 100 },
];

const SupplierTable: React.FC<SupplierTableProps> = ({
    suppliers,
    loading,
    onRowClick,
    sortConfig,
    onSort
}) => {
    /**
     * 정렬 핸들러
     */
    const handleSort = (columnKey: string, sortable: boolean) => {
        if (!sortable) return;
        onSort(columnKey);
    };

    /**
     * 정렬 아이콘 렌더링
     */
    const renderSortIcon = (columnKey: string, sortable: boolean) => {
        if (!sortable) return null;

        const isSorted = sortConfig.columnKey === columnKey;
        const direction = isSorted ? sortConfig.direction : null;

        const iconClass = direction === 'asc' ? 'fa-sort-up' : direction === 'desc' ? 'fa-sort-down' : 'fa-sort';

        return (
            <span className="sort-icon">
                <i className={`fas fa-fw ${iconClass}`} style={{ opacity: direction ? 1 : 0.3 }}></i>
            </span>
        );
    };

    return (
        <div className="table-container">
            <table className="supplier-selection-table sortable-table">
                <thead>
                    <tr>
                        {TABLE_HEADERS.map(header => (
                            <th
                                key={header.key}
                                style={{ width: `${header.width}px` }}
                                className={`${header.sortable ? 'sortable-header' : ''} ${sortConfig.columnKey === header.key ? 'sorted' : ''}`}
                                onClick={() => handleSort(header.key, header.sortable)}
                            >
                                {header.label}
                                {renderSortIcon(header.key, header.sortable)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {!loading && suppliers.length === 0 ? (
                        <tr>
                            <td colSpan={TABLE_HEADERS.length} className="text-center text-gray-600">
                                조회된 협력사가 없습니다.
                            </td>
                        </tr>
                    ) : (
                        suppliers.map((supplier) => (
                                <tr key={supplier.id} onClick={() => onRowClick(supplier)}>
                                    <td>{supplier.suppType || '-'}</td>
                                    <td>{supplier.standardArea || '-'}</td>
                                    <td>{supplier.slotSetCnt || '-'}</td>
                                    <td>
                                        {supplier.grade && supplier.grade !== '등급없음' && supplier.grade !== '-' ? (
                                            <span className={`badge ${getGradeBadgeClass(supplier.grade)}`}>
                                                {supplier.grade}
                                            </span>
                                        ) : (
                                            supplier.grade || '-'
                                        )}
                                    </td>
                                    <td>{supplier.name}</td>
                                    <td>{supplier.id}</td>
                                    <td>{supplier.phoneNumber}</td>
                                    <td>{supplier.name}</td>
                                    <td>{supplier.ratePlan}</td>
                                    <td>{supplier.registrationDate}</td>
                                    <td>{supplier.endDate}</td>
                                    <td>{supplier.ownerName}</td>
                                    <td>{supplier.companyName}</td>
                                    <td>{supplier.businessNumber}</td>
                                    <td>{supplier.accountHolder}</td>
                                    <td>{supplier.bankName}</td>
                                    <td>{supplier.accountNumber}</td>
                                    <td>{supplier.email}</td>
                                    <td>{supplier.address}</td>
                                    <td>{supplier.bizType}</td>
                                    <td>{supplier.bizItem}</td>
                                    <td>{supplier.taxType}</td>
                                </tr>
                            ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default SupplierTable;
